/**
 *   This is a service for a specific store module which uses the api.ts Axios instance as a base.
 *   We separate our files for easy managment and readibility.
 *   We export this as a service to be used in other areas of the application.
 */

// --- Models ---
import { SubjectMarkPair } from '@/models/Subject.model';

// --- Services ---
import Api from '@/services/api';
import AppService from '@/services/app';

// --- Store ---
import Store from '@/store/store';

export default {

  async logSupportDebug(message: string) {
    if (!process.env.VUE_APP_ISLIVE) {
      // tslint:disable-next-line:no-console
      console.log(`${new Date().toISOString().split('.')[0].replace('T', ' ')} - ` + message);
    }
  },

  // --- Login/Logout ---
  login(params: { username: string; password: string }) {
    return Api().post('/auth/login', params);
  },

  logout() {
    Api().post('/auth/logout').then((response) => {
      AppService.successHandler(response.data.message);
      // emptying the store now
      Store.dispatch('app/reset');
      Store.dispatch('users/resetLoggedInUser', {});

      // redirecting the user back to login
      window.location.href = Store.getters['app/urls'].login;

    });  
  },

  // --- Registration ---
  register(params: { partnerDomain: string, firstName: string, surname: string, idNumber?: string, birthDate?: string, raceId?: number, 
    genderId?: number, locationId?: number, cell?: string, email: string, username: string, password: string, isMomentumMember: boolean, 
    wantsNews: boolean }) {
    return Api().post('/auth/register', params);
  },

  createUpdateStatusInformation(params: { statusId: number, currentJob?: string, jobChangeWanted?: boolean, appliedToProgram?: boolean, 
    qualificationId?: number, preferredCompanyId?: number, gapPlans?: string, plans?: string, furtherStudy?: boolean, 
    registeredForTeriary?: boolean, averageGradeId?: number, schoolName?: string, aspectIds?: number[], selectedSubjects?: SubjectMarkPair[] }) {
    return Api().post('/auth/create_information', params);
  },

  // --- Validating User ---
  async getLoggedInUser() {
    try {
      const response = await Api().get('/auth/get_logged_in_user');
      if (response && response.data) {
        if (response.data.result && response.data.result === 'false') {
          Store.dispatch('users/resetLoggedInUser', {});
          return response.data.message;
        } else {
          Store.dispatch('users/setLoggedInUser', {
            loggedInUser: {
              loggedIn: true,
              displayName: response.data.displayName,
              email: response.data.email,
              registered: response.data.registered,
              isMomentumMember: response.data.isMomentumMember,
              paid: response.data.paid,
              testComplete: response.data.testComplete,
            },
          });
          return '';
        }
      } else {
        // response is undefined or has no data field - SHOULD NEVER HAPPEN!
        throw new Error('response: ' + JSON.stringify(response));
      }

    } catch (error) {
      Store.dispatch('users/resetLoggedInUser', {});
      return Store.getters['app/messages'].couldNotConnect;
    }
  },

  // --- Promo Code ---
  checkPromoCode(params: { promoCode: number; }) {
    return Api().post('/auth/promo_code', params);
  },

  // --- Payment ---
  createTransaction(params: { amount: number; }) {
    return Api().post('/auth/transaction_create', params);
  },

  confirmPayment() {
    return Api().get('/auth/confirm_payment');
  },

  forgotPasswordByEmail(params: { email: string; }) {
    return Api().post('/auth/password_forgot_by_email', params);
  },

  forgotPasswordByUsername(params: { email: string, username: string }) {
    return Api().post('/auth/password_forgot_by_username', params);
  },

  setPassword(params: { password: string, token: string; }) {
    return Api().post('/auth/password_set', params);
  },

  async getProfile() {
    try {
      const response = await Api().get('/auth/profile');
      if (response && response.data) {
        if (response.data.result && response.data.result === 'false') {
          Store.dispatch('users/resetLoggedInUserProfile', {});
          return response.data.message;
        } else {     
          Store.dispatch('users/setLoggedInUserProfile', {
            loggedInUserProfile: {
              firstName: response.data.firstName,
              surname: response.data.surname,
              idNumber: response.data.idNumber,
              birthDate: (response.data.birthDate !== '1900-01-01' ? response.data.birthDate : undefined),
              raceId: response.data.raceId,
              race: response.data.race,
              genderId: response.data.genderId,
              gender: response.data.gender,
              locationId: response.data.locationId,
              location: response.data.location,
              cell: response.data.cell,
              email: response.data.email,
              username: response.data.username,
              wantsNews: response.data.wantsNews,
              quote: response.data.quote,
            },
          });
          return '';
        }
      } else {
        // response is undefined or has no data field - SHOULD NEVER HAPPEN!
        throw new Error('response: ' + JSON.stringify(response));
      }

    } catch (error) {
      Store.dispatch('users/resetLoggedInUserProfile', {});
      if (error.response) {
        return error.response.statusText;
      } else {
        return Store.getters['app/messages'].couldNotConnect;
      }
    }
  },

  updateProfile(params: { firstName: string, surname: string, idNumber?: string, 
      birthDate?: string, raceId?: number, genderId?: number, locationId?: number, 
      cell?: string, email: string, wantsNews: boolean }) {
    return Api().post('/auth/update_profile', params);
  },

  async resetPassword() {
    return Api().post('/auth/reset_password');
  },

  async deleteMe() {
    return Api().post('/auth/delete');
  },

  async getStatusInformation() {
    return Api().get('/auth/information');
  },

  async getSubjectSelection() {
    return Api().get('/auth/information_subjects');
  },

  async getAspectSelection() {
    return Api().get('/auth/information_aspects');
  },

  async getReport() {
    return Api().get('/report/get_filename');
  },

  async createReport() {
    return Api().get('/report/create');
  },

  async emailReport() {
    return Api().get('/report/email');
  },

};
